'use client';

import { cn } from '@holistic/components';
import { Check, Truck } from '@holistic/components/icons';

import type { ShippingMethod } from '@/types/cargo';
import type { ComponentType } from 'react';

type MethodOption = {
  value: ShippingMethod;
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  badge?: string;
};

/**
 * Şu an tek yöntem var (bkz. types/cargo.ts). Liste bilerek dizi olarak
 * duruyor: ikinci yöntem açıldığında buraya bir kart eklemek yeterli, ekranın
 * kalanı değişmiyor.
 */
const OPTIONS: MethodOption[] = [
  {
    value: 'pickup',
    icon: Truck,
    title: 'Kapımdan Teslim Alınsın',
    description:
      'HepsiJET kuryesi seçeceğiniz randevu gününde adresinize gelerek cihazı kapınızdan teslim alsın.',
    badge: 'Ücretsiz',
  },
];

type ShippingMethodSelectorProps = {
  value: ShippingMethod;
  onChange: (method: ShippingMethod) => void;
};

/** Gönderim yöntemi seçimi. Seçim yalnızca istemcide durur; talebe gitmez. */
const ShippingMethodSelector = ({
  value,
  onChange,
}: ShippingMethodSelectorProps) => (
  <div role="radiogroup" className="grid gap-2.5 sm:grid-cols-2">
    {OPTIONS.map(({ value: method, icon: Icon, title, description, badge }) => {
      const isSelected = value === method;

      return (
        <button
          key={method}
          type="button"
          role="radio"
          aria-checked={isSelected}
          onClick={() => onChange(method)}
          className={cn(
            'group relative flex cursor-pointer flex-col gap-2.5 rounded-2xl border p-4 text-left',
            'transition-all duration-200',
            'focus-visible:ring-primary/30 focus-visible:ring-2 focus-visible:outline-none',
            isSelected
              ? 'border-primary bg-primary/5 shadow-primary/10 shadow-sm'
              : 'border-gray-100 bg-white hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-sm'
          )}
        >
          <span className="flex items-center gap-3">
            <span
              className={cn(
                'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
                isSelected
                  ? 'bg-primary/10 text-primary'
                  : 'bg-gray-50 text-gray-400'
              )}
            >
              <Icon className="size-4.5" />
            </span>

            <span className="min-w-0 flex-1 text-sm font-semibold text-gray-900">
              {title}
            </span>

            <span
              className={cn(
                'flex size-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                isSelected
                  ? 'border-primary bg-primary text-white'
                  : 'border-gray-200 bg-white'
              )}
            >
              {isSelected && <Check className="size-3" strokeWidth={3} />}
            </span>
          </span>

          <span className="text-xs leading-relaxed text-gray-500">
            {description}
          </span>

          {badge && (
            <span className="bg-primary/10 text-primary self-start rounded-full px-2.5 py-1 text-[11px] font-semibold">
              {badge}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default ShippingMethodSelector;
